import React, {Suspense, useEffect} from "react";
import {getJsonFieldValue, v56a_checkTokenValidity, v56a_getAssets, v56a_getContactInfo, v56a_getProfiles} from "../../v56_api";
import {useRecoilState} from "recoil";
import {loadingText_, profileBucketSettings_, profiles, selectedProfileBuckets_, selectedProfileReadOnlyVDrives_, selectedProfileVDrives_, vdrives} from "../../App";
import V56Spinner from "../../components/loaderIndicators/v56_spinner";
import {RefreshIcon} from "@heroicons/react/outline";
import {PlusIcon} from "@heroicons/react/solid";

export default function V56ProfileList() {

    const [showProfileFlyout, setShowProfileFlyout] = React.useState(false);
    const [activeProfile, setActiveProfile] = React.useState(null);
    const [getProfiles, setProfiles] = useRecoilState(profiles);
    const [loadingText, setLoadingText] = useRecoilState(loadingText_);
    const [vDrives, setVDrives] = useRecoilState(vdrives);
    const [selectedVDrives, setSelectedVDrives] = useRecoilState(selectedProfileVDrives_)
    const [profileSettings, setProfileSettings] = useRecoilState(profileBucketSettings_);
    const [selectedBuckets, setSelectedBuckets] = useRecoilState(selectedProfileBuckets_);
    const [selectedReadOnlyVDrives, setSelectedReadOnlyVDrives] = useRecoilState(selectedProfileReadOnlyVDrives_)

    const V56EditProfile = React.lazy(() => import('../../components/editProfile'));

    useEffect(() => {
        // Get profiles
        (async () => {
            const tokenIsValid = await v56a_checkTokenValidity();
            if (!tokenIsValid) {
                window.location.href = ('https://secure.vault56.com');
                return;
            }

            setLoadingText('Loading vDrive profiles...');

            //region Get asset information
            const vd = await v56a_getProfiles();
            if (vd.status === 200) {
                setProfiles(vd.data);
            }
            setLoadingText('');
        })();

        // Get vDrives
        (async () => {
            const tokenIsValid = await v56a_checkTokenValidity();
            if (!tokenIsValid) {
                window.location.href = ('https://secure.vault56.com');
                return;
            }

            //region Get asset information
            const vd = await v56a_getAssets();
            if (vd.status === 200) {
                setVDrives(vd.data);
            }

        })();

    }, []);

    function formatDate(dateString) {
        if (dateString === null) {
            return 'Never';
        }
        let d = new Date(dateString);
        let retVal = d.getFullYear() + "-";
        retVal += ("0"+(d.getMonth()+1)).slice(-2) + "-";
        retVal += ("0" + d.getDate()).slice(-2) + " ";
        retVal += ("0" + d.getHours()).slice(-2) + ":";
        retVal += ("0" + d.getMinutes()).slice(-2);


        return retVal;
    }


    function setFlyoutCb(value) {
        setShowProfileFlyout(value);
    }

    function showProfile(profile) {
        //console.log(profile);
        setActiveProfile(profile);
        // Make a copy of properties-object
        let profileSettings = JSON.parse(profile.properties);
        setSelectedBuckets(profileSettings.buckets);
        setSelectedVDrives(profileSettings.vDrives);
        setProfileSettings(profileSettings.bucketSettings);
        setSelectedReadOnlyVDrives((profileSettings.readOnlyVDrives === undefined) ? [] : profileSettings.readOnlyVDrives);
        setShowProfileFlyout(true);
    }

    const ColoredLine = ({ color }) => (
        <hr
            style={{
                color: color,
                backgroundColor: color,
                height: 5
            }}
        />
    );

    const renderFlyout = React.useCallback(() => {
        if (activeProfile !== null) {
            return <Suspense><V56EditProfile newProfile={false} open={showProfileFlyout} activeProfile={activeProfile} activationName={activeProfile.profile_name} setOpen={setFlyoutCb}/></Suspense>
        }
        return <Suspense><V56EditProfile newProfile={true} open={showProfileFlyout} activeProfile={null} activationName={''} setOpen={setFlyoutCb}/></Suspense>
    }, [showProfileFlyout, activeProfile]);

    const renderContent = React.useCallback(() => {

        function reloadAssets() {
            setLoadingText('Loading vDrive profiles...');
            v56a_getProfiles().then((vd) => {
                if (vd.status === 200) {
                    setProfiles(vd.data);
                }
                setLoadingText('');
            });
        }

        return(
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-base font-semibold leading-6 text-gray-900">vDrive Enterprise profiles</h1>
                        <p className="mt-2 text-sm text-gray-700">
                            This is a list of your vDrive Enterprise profiles. Create a new profile by clicking the add button, then <a href="https://cdn.vault56.com/vDriveSetup.exe">install vDrive</a> on a destination computer and enter the profile activation code.
                        </p>
                    </div>
                </div>
                <ColoredLine color="#e6581c" />
                <div className="mt-4 sm:ml-16 float-right">
                    <button
                        type="button"
                        className="block rounded-md bg-[#5f2881] py-2 px-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#6f4488] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6f4488]"
                        onClick={() => {
                            setSelectedVDrives([]);
                            setSelectedBuckets([]);
                            setProfileSettings({});
                            setActiveProfile(null);
                            setShowProfileFlyout(true);
                        }}
                    >
                        <div className="flex-1 flex">
                            <PlusIcon width="20" height="20"/>
                            <span className="ml-2">Add profile</span>
                        </div>
                    </button>
                </div>

                <div className="-mx-4 mt-8 sm:-mx-0">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                Name
                            </th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                                <span className="float-right cursor-pointer"
                                      onClick={reloadAssets}
                                > <RefreshIcon width={20} height={20}/> </span>
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {getProfiles.map((profile) => (
                            <tr key={profile.id}>
                                <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0">
                                    {profile.profile_name}
                                    <dl className="font-normal lg:hidden">
                                        <dt className="sr-only">{profile.profile_name}</dt>
                                    </dl>
                                </td>
                                <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                    <a href="#" className="text-indigo-600 hover:text-indigo-900"
                                       onClick={() => {
                                           showProfile(profile);
                                       }}
                                    >
                                        Edit<span className="sr-only">, {profile.name}</span>
                                    </a>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }, [getProfiles]);
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {loadingText!==''?<V56Spinner/>:<>
                {renderContent()}
                {renderFlyout()}
            </>}
        </div>
    )
}
